import { type User } from '@/lib/types';
import useUserAutocomplete from './useUserAutocomplete';
import validator from 'validator';
import useAuthenticatedUser from '@/lib/auth/useAuthenticatedUser';
import { useContext } from 'react';
import ChallengeContext from '@/components/ChallengePortfolio/NormalView/Challenge/ChallengeView/ChallengeContext';

const fullUsersMap = new Map<string, User>();

const useUserAutocompleteOptions = (
  query: string,
): [string[], Map<string, User>] => {
  const userAutocompleteSuggestions = useUserAutocomplete(query);

  for (const user of userAutocompleteSuggestions) {
    if (!fullUsersMap.has(user.email)) {
      fullUsersMap.set(user.email, user);
    }
  }

  const authenticatedUser = useAuthenticatedUser()!;
  const [challenge] = useContext(ChallengeContext);
  const allChallengeUsers = [...challenge.users, ...challenge.invitees];

  const autocompleteBaseOptions = userAutocompleteSuggestions
    .map(autocompleteOption => autocompleteOption.email)
    .filter(email => email !== authenticatedUser.email)
    // When user is already added, don't show the fancy user preview but still
    // allow the user to "force" re-add the user by direct email invite (see
    // below) to handle users that remain faultily marked as still attached to
    // the challenge.
    .filter(email => !allChallengeUsers.some(user => user.email === email));

  const queryIsValidEmail = query.trim().length > 0 && validator.isEmail(query);
  const shouldAddCustomEmailOption =
    queryIsValidEmail &&
    !autocompleteBaseOptions.includes(query) &&
    query !== authenticatedUser.email;

  const autocompleteOptions = shouldAddCustomEmailOption
    ? [query, ...autocompleteBaseOptions]
    : autocompleteBaseOptions;

  return [autocompleteOptions, fullUsersMap];
};

export default useUserAutocompleteOptions;
